import {cloneDeep} from 'lodash';
import socketIOClient from 'socket.io-client';
import {CRUDType, DropdownOption} from '../components/Data/DataConst';
import config from '../config/config';
import {DataSubType, DataType} from '../const/crud';
import {Company} from '../models/Company';
import {Customer} from '../models/Customer';
import {Job} from '../models/Job';
import {User} from '../models/User';
import {DataHelper} from './dataHelper';

interface Event {
  model: DataType;
  eventType: CRUDType;
  data: any;
  company: string;
}

interface SocketSetters {
  [DataType.COMPANY]?: {
    collection: any;
    selected?: any;
    options?: any;
    total?: any;
  };
  [DataType.USER]?: {
    collection: any;
    selected?: any;
    options?: any;
    total?: any;
    count?: any;
  };
  [DataType.CUSTOMER]?: {
    collection: any;
    selected?: any;
    options?: any;
    total?: any;
  };
  [DataType.JOB]?: {
    collection: any;
    selected?: any;
    options?: any;
    total?: any;
  };
}

export interface SocketState {
  [DataType.COMPANY]?: {
    collection: Company[];
    selected?: Company;
    options?: DropdownOption[];
    total?: number;
  };
  [DataType.USER]?: {
    collection: User[];
    selected?: User;
    options?: DropdownOption[];
    total?: number;
    count?: {
      current: number;
      max: number;
    };
  };
  [DataType.CUSTOMER]?: {
    collection: Customer[];
    selected?: Customer;
    options?: DropdownOption[];
    total?: number;
  };
  [DataType.JOB]?: {
    collection: Job[];
    selected?: Job;
    options?: DropdownOption[];
    total?: number;
  };
}

let stateSetters: SocketSetters = {};

let state: SocketState = {
  [DataType.COMPANY]: {
    collection: [],
  },
  [DataType.USER]: {
    collection: [],
  },
  [DataType.CUSTOMER]: {
    collection: [],
  },
  [DataType.JOB]: {
    collection: [],
  },
};

let systemState: any = {
  liveUpdates: {
    status: false,
  },
};

export default class SocketHelper {
  private socket: any = null;

  public connect(token, company) {
    // Dispose of any existing socket before creating another. Without this,
    // repeated calls leave orphaned sockets alive in the same browser tab.
    this.disconnect();

    const socket = socketIOClient(config.socket, {
      // Use socket.io's own reconnection, which backs off and jitters.
      // The previous hand-rolled 3s retry loop could never be cancelled.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
      timeout: 20000,
      transports: ['websocket', 'polling'],
    });

    this.socket = socket;

    // Fires on the first connection and on every reconnection, so the auth
    // handshake repeats automatically.
    socket.on('connect', () => {
      socket.emit('authentication', {
        token,
        company,
      });
    });

    // Registered once each. These previously sat inside the `authenticated`
    // handler, which socketio-auth fires on every reconnection, so another
    // copy of all three was added each time the socket came back.
    socket.on('successfullyAuthenticated', () => {
      console.info('Socket connected');
      if (systemState.liveUpdates.setStatus) {
        systemState.liveUpdates.setStatus(true);
      }
    });

    socket.on('disconnect', (reason) => {
      console.info(`Socket disconnected: ${reason}`);
      if (systemState.liveUpdates.setStatus) {
        systemState.liveUpdates.setStatus(false);
      }

      // Do NOT call this.connect() here. socket.io reconnects on its own.
      // The one exception is an explicit server-side disconnect, which does
      // not trigger automatic reconnection.
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    socket.on('event', (event) => {
      this.processEvent(event);
    });

    socket.on('unauthorized', (err) => {
      console.error('Socket authentication failed', err);
    });

    socket.on('connect_error', (err) => {
      console.warn('Socket connect error', err && err.message);
    });

    return socket;
  }

  public disconnect() {
    if (!this.socket) {
      return;
    }

    this.socket.removeAllListeners();
    this.socket.close();
    this.socket = null;
  }

  public getState() {
    return state;
  }

  public addSetters(setters: SocketSetters) {
    stateSetters = setters;
  }
  public addState(newState) {
    state = newState;
  }
  public updateState(dataType, dataSubType, data) {
    if (state && state[dataType]) {
      state[dataType][dataSubType] = data;
    }
  }

  public addLiveUpdateSetter(setter) {
    systemState.liveUpdates.setStatus = setter;
  }

  public processEvent(event: Event) {
    switch (event.eventType) {
      case CRUDType.CREATE:
        if (!stateSetters[event.model]) {
          break;
        }

        const clonedForCreation = cloneDeep(
          state[event.model][DataSubType.COLLECTION],
        );

        clonedForCreation.unshift(event.data);

        stateSetters[event.model][DataSubType.COLLECTION](clonedForCreation);
        this.updateState(
          event.model,
          DataSubType.COLLECTION,
          clonedForCreation,
        );

        // Build options
        if (stateSetters[event.model].options) {
          SocketHelper.buildSelectOptions(event.model, clonedForCreation);
        }

        // Set call for total
        if (stateSetters[event.model].total) {
          stateSetters[event.model].total();
        }

        break;
      case CRUDType.DELETE:
        if (!stateSetters[event.model]) {
          break;
        }

        let clonedForDeletion = cloneDeep(
          state[event.model][DataSubType.COLLECTION],
        );

        clonedForDeletion = clonedForDeletion.filter(
          (item) => item._id !== event.data._id,
        );

        stateSetters[event.model][DataSubType.COLLECTION](clonedForDeletion);
        this.updateState(
          event.model,
          DataSubType.COLLECTION,
          clonedForDeletion,
        );

        // Build options
        if (stateSetters[event.model].options) {
          SocketHelper.buildSelectOptions(event.model, clonedForDeletion);
        }

        // Set call for total
        if (stateSetters[event.model].total) {
          stateSetters[event.model].total();
        }

        break;
      case CRUDType.UPDATE:
        if (!stateSetters[event.model]) {
          break;
        }

        let clonedForUpdate = cloneDeep(
          state[event.model][DataSubType.COLLECTION],
        );
        clonedForUpdate = clonedForUpdate.map((item) => {
          if (item._id === event.data._id) {
            item = event.data;
          }
          return item;
        });

        stateSetters[event.model][DataSubType.COLLECTION](clonedForUpdate);
        this.updateState(event.model, DataSubType.COLLECTION, clonedForUpdate);

        // Set selected
        if (
          stateSetters[event.model][DataSubType.SELECTED] &&
          state[event.model][DataSubType.SELECTED]._id === event.data._id
        ) {
          stateSetters[event.model][DataSubType.SELECTED](event.data);
        }

        // Build options
        if (stateSetters[event.model].options) {
          SocketHelper.buildSelectOptions(event.model, clonedForUpdate);
        }
        break;
      case CRUDType.COUNT:
        if (!stateSetters[event.model]) {
          break;
        }

        stateSetters[event.model].count(event.data);
        break;
    }
  }

  private static buildSelectOptions(dataType: DataType, dataSet: any[]) {
    let options: DropdownOption[];
    const stateSetter = stateSetters[dataType];
    switch (dataType) {
      case DataType.COMPANY:
        if (stateSetter.options) {
          options = DataHelper.buildCompanySelectOptions(dataSet);
          stateSetter.options(options);
        }
        break;
      case DataType.USER:
        if (stateSetter.options) {
          options = DataHelper.buildUserSelectOptions(dataSet);
          stateSetter.options(options);
        }
        break;
      case DataType.CUSTOMER:
        if (stateSetter.options) {
          options = DataHelper.buildUserSelectOptions(dataSet);
          stateSetter.options(options);
        }
        break;
    }
  }
}
